// Under Development - not live yet
import { useState, useEffect } from "react";
import Header from "../components/Header.js";
import Footer from "../components/Footer.js";
import {
  fetchVacancyPositions,
  validateJobApplicationForm,
} from "../utils/jobApplicationUtils.js";
import { getAppCheckToken } from "../utils/appCheck.js";

export default function JobApplication() {
  const [positions, setPositions] = useState([]);
  const [verifiedMsg, setVerifiedMsg] = useState("");
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    position: "",
    startDate: "",
    employment: "",
    resume: null,
    details: "",
    verified: false,
    address: "",
    agency: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    async function getPositions() {
      const pos = await fetchVacancyPositions();
      setPositions(pos);
    }
    getPositions();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    if (type === "checkbox") {
      setForm({ ...form, [name]: checked });
    } else if (type === "file") {
      setForm({ ...form, resume: files[0] });
    } else {
      setForm({ ...form, [name]: value });
    }
  };

  const handleRobotCheck = async (e) => {
    if (e.target.checked) {
      try {
        await getAppCheckToken();
        setForm({ ...form, verified: true });
        setVerifiedMsg("✔️ Verified!");
      } catch (error) {
        setForm({ ...form, verified: false });
        setVerifiedMsg("❌ Verification failed. Please try again.");
        alert("reCAPTCHA verification failed. Please try again.");
      }
    } else {
      setForm({ ...form, verified: false });
      setVerifiedMsg("");
    }
  };

  const BUCKET_NAME = "job-resume-bucket";

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationError = validateJobApplicationForm(form);
    if (validationError) {
      setError(validationError);
      return;
    }
    if (!form.verified) {
      setError("Please verify you are not a robot.");
      return;
    }
    setError("");

    try {
      const appCheckToken = await getAppCheckToken();

      // Step 1: Get signed upload URL from backend
      const file = form.resume;
      const fileName = `${Date.now()}_${file.name}`;
      const contentType = file.type;

      const urlRes = await fetch(
        "https://asia-south2-barma-sorig.cloudfunctions.net/getResumeUploadUrl",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Firebase-AppCheck": appCheckToken,
          },
          body: JSON.stringify({ fileName, contentType }),
        }
      );
      const { url } = await urlRes.json();

      // Step 2: Upload file directly to Cloud Storage
      const uploadRes = await fetch(url, {
        method: "PUT",
        headers: {
          "Content-Type": contentType,
        },
        body: file,
      });

      if (!uploadRes.ok) {
        setError("Failed to upload resume.");
        return;
      }

      // Step 3: Send form data (without file) to backend
      const payload = {
        ...form,
        resumeUrl: `gs://${BUCKET_NAME}/resumes/${fileName}`,
      };
      delete payload.resume;

      const response = await fetch(
        "https://asia-south2-barma-sorig.cloudfunctions.net/sendJobApplicationEmail",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Firebase-AppCheck": appCheckToken,
          },
          body: JSON.stringify(payload),
        }
      );

      const result = await response.json();

      if (result.success) {
        setSubmitted(true);
      } else {
        setError(result.error || "Submission failed.");
      }
    } catch (error) {
      setError("There was an error submitting your application.");
    }
  };

  return (
    <>
      {/* <button
        type="button"
        onClick={async () => {
          await uploadVacancyPositionsToFirestore();
          alert("Vacancy positions uploaded!");
        }}
      >
        Upload Vacancy Positions to Firestore
      </button> */}
      <Header />
      <main className="min-h-screen py-12 bg-gradient-to-br from-indigo-900 via-blue-900 to-sky-900 flex items-center justify-center">
        <div className="max-w-2xl w-full bg-white/90 rounded-2xl shadow-2xl p-8 border border-amber-100">
          <div className="flex flex-col items-center mb-8">
            <h1 className="text-3xl font-bold text-amber-700 mb-2 text-center">
              Job Application Form
            </h1>
            <p className="text-lg text-gray-700 text-center">
              Please fill out all required fields to apply for a position.
            </p>
          </div>
          {submitted ? (
            <div className="text-green-700 text-center font-semibold text-lg">
              Thank you for applying! We will contact you soon.
            </div>
          ) : (
            <form className="space-y-6" onSubmit={handleSubmit}>
              {error && (
                <div className="text-red-600 font-semibold text-center mb-2">
                  {error}
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block font-medium text-gray-700 mb-1">
                    First Name
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    value={form.firstName}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    placeholder="i.e. Metho"
                  />
                </div>
                <div>
                  <label className="block font-medium text-gray-700 mb-1">
                    Last Name
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    value={form.lastName}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    placeholder="i.e. Selden"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block font-medium text-gray-700 mb-1">
                    Email 📧
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    placeholder="Your email address here"
                  />
                </div>
                <div>
                  <label className="block font-medium text-gray-700 mb-1">
                    Phone
                  </label>
                  <input
                    type="text"
                    name="phone"
                    value={form.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    placeholder="17XXXXXX or 77XXXXXX"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block font-medium text-gray-700 mb-1">
                    What position are you applying for?
                  </label>
                  <select
                    name="position"
                    value={form.position}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="">Please select</option>
                    {positions.map((pos) => (
                      <option key={pos} value={pos}>
                        {pos}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block font-medium text-gray-700 mb-1">
                    Available Start Date
                  </label>
                  <input
                    type="date"
                    name="startDate"
                    value={form.startDate}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
              </div>
              <div>
                <label className="block font-medium text-gray-700 mb-2">
                  What is your current employment status?
                </label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {["Employed", "Self-Employed", "Unemployed", "Student"].map(
                    (status) => (
                      <label
                        key={status}
                        className={`flex items-center px-3 py-2 rounded-lg border cursor-pointer ${
                          form.employment === status
                            ? "bg-amber-100 border-amber-600 font-bold"
                            : "bg-gray-50 border-gray-300"
                        }`}
                      >
                        <input
                          type="radio"
                          name="employment"
                          value={status}
                          checked={form.employment === status}
                          onChange={handleChange}
                          className="mr-2"
                          required
                        />
                        {status}
                      </label>
                    )
                  )}
                </div>
              </div>
              <div>
                <label className="block font-medium text-gray-700 mb-1">
                  Upload your resume
                </label>
                <input
                  type="file"
                  name="resume"
                  accept=".pdf,.doc,.docx"
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white"
                />
                {form.resume && (
                  <div className="mt-2 text-green-700 text-sm">
                    Selected file: {form.resume.name}
                  </div>
                )}
              </div>
              <div>
                <label className="block font-medium text-gray-700 mb-1">
                  Present Address
                </label>
                <input
                  type="text"
                  name="address"
                  value={form.address}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  placeholder="eg. Chubachu, Thimphu, Bhutan"
                />
              </div>
              <div>
                <label className="block font-medium text-gray-700 mb-1">
                  Previous/Present Working Agency Name
                </label>
                <input
                  type="text"
                  name="agency"
                  value={form.agency}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  placeholder="eg. XYZ Pvt. Ltd."
                />
              </div>
              <div>
                <label className="block font-medium text-gray-700 mb-1">
                  DETAILS
                </label>
                <textarea
                  name="details"
                  value={form.details}
                  onChange={handleChange}
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  placeholder="Write any additional details here..."
                />
              </div>
              <div className="flex items-center mt-4">
                <input
                  type="checkbox"
                  name="verified"
                  checked={form.verified}
                  onChange={handleRobotCheck}
                  className="mr-2"
                />
                <span className="text-gray-700 font-medium">
                  I'm not a robot
                </span>
                {verifiedMsg && (
                  <span
                    className={`ml-3 font-semibold ${
                      form.verified ? "text-green-600" : "text-red-600"
                    }`}
                  >
                    {verifiedMsg}
                  </span>
                )}
              </div>
              <button
                type="submit"
                className="w-full bg-amber-600 text-white font-bold py-3 rounded-lg shadow hover:bg-amber-700 transition mt-6"
              >
                Send Application
              </button>
            </form>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}
